// src/admin/Settings.jsx
import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";

const setAdminRoleFn = httpsCallable(functions, "setAdminRole");

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.set-grid { display:grid; grid-template-columns:1fr 1fr; gap:18px; }

.set-card {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; padding:22px; margin-bottom:14px;
}
.set-card-title { font-size:14px; font-weight:700; color:#fff; margin-bottom:16px; }
.set-card.danger { background:rgba(239,68,68,0.03); border-color:rgba(239,68,68,0.15); }
.set-card.danger .set-card-title { color:#ef4444; }

.set-inp {
  width:100%; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.08); border-radius:6px;
  padding:10px 12px; color:#fff;
  font-family:'Inter',sans-serif; font-size:13px; outline:none;
  margin-bottom:14px; transition:border-color 0.15s;
}
.set-inp:focus { border-color:rgba(0,120,242,0.4); }
.set-lbl {
  display:block; font-size:10px; font-weight:700; color:#6b6b6b;
  letter-spacing:1px; text-transform:uppercase; margin-bottom:6px;
}

/* Toggle */
.set-toggle-row {
  display:flex; align-items:center; gap:10px;
  margin-bottom:18px; cursor:pointer; user-select:none;
}
.set-toggle-track {
  width:36px; height:20px; border-radius:10px;
  position:relative; transition:background 0.2s; flex-shrink:0;
}
.set-toggle-thumb {
  width:14px; height:14px; background:#fff; border-radius:50%;
  position:absolute; top:3px; transition:left 0.2s;
}

/* Buttons */
.set-btn {
  padding:11px 22px; background:#0078F2; border:none; border-radius:6px;
  color:#fff; font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  cursor:pointer; transition:all 0.15s;
}
.set-btn:hover:not(:disabled) { background:#0a84ff; }
.set-btn:disabled { opacity:0.4; cursor:not-allowed; }
.set-btn.saved { background:rgba(34,197,94,0.15); border:1px solid rgba(34,197,94,0.3); color:#22c55e; }

.set-btn-role {
  padding:11px 22px; background:rgba(0,120,242,0.08);
  border:1px solid rgba(0,120,242,0.2); border-radius:6px;
  color:#0078F2; font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  cursor:pointer; transition:all 0.15s; width:100%;
}
.set-btn-role:hover:not(:disabled) { background:rgba(0,120,242,0.14); }
.set-btn-role:disabled { opacity:0.4; cursor:not-allowed; }

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .set-grid  { grid-template-columns:1fr; gap:0; }
  .set-card  { padding:16px; }
  .set-btn, .set-btn-role { width:100%; }
}
`;

export default function Settings() {
  const [settings, setSettings] = useState({
    storeName:"PhsarGame", announcement:"", contactTelegram:"", contactEmail:"", maintenanceMode:false
  });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [targetUid,   setTargetUid]   = useState("");
  const [roleAction,  setRoleAction]  = useState("admin");
  const [roleSaving,  setRoleSaving]  = useState(false);

  useEffect(() => {
    getDoc(doc(db, "settings", "general")).then(snap => {
      if (snap.exists()) setSettings(s => ({ ...s, ...snap.data() }));
      setLoading(false);
    });
  }, []);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true); setSaved(false);
    try {
      await updateDoc(doc(db, "settings", "general"), { ...settings, updatedAt:serverTimestamp() });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) { alert("Save failed: " + err.message); }
    finally { setSaving(false); }
  }

  async function handleSetRole(e) {
    e.preventDefault();
    if (!targetUid.trim()) return;
    setRoleSaving(true);
    try {
      await setAdminRoleFn({ targetUid:targetUid.trim(), role:roleAction });
      alert(`✅ User ${targetUid} role set to ${roleAction}`);
      setTargetUid("");
    } catch (err) { alert("Error: " + err.message); }
    finally { setRoleSaving(false); }
  }

  return (
    <>
      <style>{S}</style>
      <div style={{ fontSize:16, fontWeight:800, color:"#fff", marginBottom:20 }}>⚙️ Settings</div>

      <div className="set-grid">
        {/* ── Left: Store settings ── */}
        <div>
          <div className="set-card">
            <div className="set-card-title">Store Settings</div>
            {loading ? (
              <div style={{ color:"#6b6b6b", fontSize:13, padding:"20px 0" }}>Loading…</div>
            ) : (
              <form onSubmit={handleSave}>
                <label className="set-lbl">Store Name</label>
                <input className="set-inp" value={settings.storeName}
                  onChange={e => setSettings(s => ({...s, storeName:e.target.value}))} />

                <label className="set-lbl">Announcement Banner (empty = hidden)</label>
                <input className="set-inp" value={settings.announcement}
                  onChange={e => setSettings(s => ({...s, announcement:e.target.value}))}
                  placeholder="🎉 New games added!" />

                <label className="set-lbl">Telegram Support Handle</label>
                <input className="set-inp" value={settings.contactTelegram}
                  onChange={e => setSettings(s => ({...s, contactTelegram:e.target.value}))}
                  placeholder="@phsargame_kh" />

                <label className="set-lbl">Support Email</label>
                <input className="set-inp" value={settings.contactEmail}
                  onChange={e => setSettings(s => ({...s, contactEmail:e.target.value}))}
                  placeholder="support@phsargame.kh" />

                {/* Maintenance toggle */}
                <div className="set-toggle-row"
                  onClick={() => setSettings(s => ({...s, maintenanceMode:!s.maintenanceMode}))}>
                  <div className="set-toggle-track"
                    style={{ background:settings.maintenanceMode ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.12)" }}>
                    <div className="set-toggle-thumb" style={{ left:settings.maintenanceMode ? 19 : 3 }} />
                  </div>
                  <span style={{ fontSize:13, color:"#a3a3a3", fontWeight:500 }}>
                    Maintenance Mode —{" "}
                    <span style={{ color:"#6b6b6b", fontWeight:400 }}>
                      {settings.maintenanceMode ? "Store closed" : "Store open"}
                    </span>
                  </span>
                </div>

                <button type="submit" className={`set-btn ${saved ? "saved" : ""}`} disabled={saving}>
                  {saving ? "Saving…" : saved ? "✅ Saved!" : "Save Settings"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ── Right: Admin roles + help ── */}
        <div>
          <div className="set-card">
            <div className="set-card-title">Admin Roles</div>
            <div style={{ fontSize:12, color:"#6b6b6b", marginBottom:16, lineHeight:1.6 }}>
              Grant or revoke admin access by Firebase UID.
            </div>
            <form onSubmit={handleSetRole}>
              <label className="set-lbl">Firebase User UID</label>
              <input className="set-inp" value={targetUid}
                onChange={e => setTargetUid(e.target.value)}
                placeholder="Paste Firebase UID here" />
              <label className="set-lbl">Role</label>
              <select className="set-inp" style={{ cursor:"pointer" }}
                value={roleAction} onChange={e => setRoleAction(e.target.value)}>
                <option value="admin">admin</option>
                <option value="customer">customer (revoke admin)</option>
              </select>
              <button type="submit" className="set-btn-role"
                disabled={roleSaving || !targetUid.trim()}>
                {roleSaving ? "Updating…" : "Set Role"}
              </button>
            </form>
          </div>

          <div className="set-card danger">
            <div className="set-card-title">⚠️ How to find a User UID</div>
            <div style={{ fontSize:12, color:"#a3a3a3", lineHeight:2 }}>
              1. Go to <strong style={{color:"#fff"}}>Firebase Console</strong><br/>
              2. Authentication → Users<br/>
              3. Find the user by email<br/>
              4. Copy the <strong style={{color:"#fff"}}>User UID</strong> column<br/>
              5. Paste above and set role
            </div>
          </div>
        </div>
      </div>
    </>
  );
}